import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ApplicantType, ApplicationStatusEnum} from '@service/enum/process-state.enum';
import {ApplicantModel, ApplicationModel, KeyIdentifierModel, CustomerConsentModel,} from '@service/models';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {AppConfigService} from '@service/config/app-config.service';
import {environment} from '@env/environment';
import {UtilSessionStorageService} from '@core/util-services/util-session-storage.service';


@Injectable({
    providedIn: 'root'
})
export class SharedkeyDataService {
    keyIdentifier = new BehaviorSubject<KeyIdentifierModel>(new KeyIdentifierModel(true));
    keyIdentifier$ = this.keyIdentifier.asObservable();
    appConfig = AppConfigService.config;
    preApplicationConsents: CustomerConsentModel[] = [];
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private sessionStorageService: UtilSessionStorageService) {
        this.announceIdentifierChanges(new KeyIdentifierModel(true));
    }

    announceIdentifierChanges(identifier: KeyIdentifierModel) {
        // Referral attribution must survive a full page reload (in-memory state resets), so
        // persist it as soon as we have one, and fall back to the last persisted value when we don't.
        if (identifier.referralCode) {
            this.sessionStorageService.setReferralCode(identifier.referralCode);
        } else {
            const storedReferralCode = this.sessionStorageService.getReferralCode();
            if (storedReferralCode) {
                identifier.referralCode = storedReferralCode;
            }
        }
        this.keyIdentifier.next(identifier);
    }

    initializeIdentifier(identifier: KeyIdentifierModel): KeyIdentifierModel {
        if (identifier === undefined || identifier === null) {
            identifier = new KeyIdentifierModel(true);
        } else {
            identifier.onPageLoad = false;
        }
        return identifier;
    }

    isIdentifierAnnounced(identifier: KeyIdentifierModel): boolean {
        return !(identifier === undefined || identifier === null);
    }

    /**
     * LP-1991: Membership eligibility is already satisfied when the applicant carries a criteria value
     * that is still offered by this institution and is complete, i.e. every dependent field the
     * eligibility page would ask for is already present. Such an applicant should not be asked the
     * question again on a revisit or on a cross-sell application, the page is skipped instead.
     */
    isMembershipEligibilitySatisfied(applicant: ApplicantModel): boolean {
        const criteria = applicant && applicant.membershipEligibilityCriteria;
        if (!criteria) {
            return false;
        }
        /* the stored value must still be a selectable option for this institution */
        const options = this.appConfig?.membershipEligibilityOptions || [];
        if (!options.some(option => option.value === criteria)) {
            return false;
        }
        /* PQS-9048: an option that asks for the business name is incomplete without it */
        if (options.some(option => option.value === criteria && option.askBusinessName === true)
            && this.isBlankValue(applicant.affiliatedSponsorOrganization)) {
            return false;
        }
        switch (criteria) {
            case 'AFFILIATED_ORGANIZATION':
            case 'FAMILY_MEMBER_AFFILIATED_ORGANIZATION':
                return !this.isBlankValue(applicant.affiliatedSponsorOrganization);
            case 'FAMILY_MEMBER_LIST':
                return !this.isBlankValue(applicant.relationShipToFamilyMember);
            case 'FAMILY_MEMBER':
                return !this.appConfig?.membershipEligibilityCriteria?.familyMemberCustomerIdentifier
                    || !this.isBlankValue(applicant.familyMemberCustomerIdentifier);
            case 'STUDENT':
                return !this.isBlankValue(applicant.studentId);
            default:
                /* DEROS date is optional on the page itself, hence MILITARY needs no extra data */
                return true;
        }
    }

    private isBlankValue(value: any): boolean {
        return value === undefined || value === null || value.toString().trim() === '';
    }

    /**
     * Co-applicants complete their journey on the /consent page. When coBoAdditionalInfoOnReview is set,
     * the /consent page hands over to /review so the co-applicant fills the additional information step
     * there (employment and residence history, plus ID when collectID is on for the product), and the
     * final consent, consent PDF and fraud check happen at the end of /review instead.
     */
    isReviewPageRequiredForCoApplicant(identifier: KeyIdentifierModel): boolean {
        // The modules live on the additional information step, so there is nowhere to send the
        // co-applicant when that step is switched off for the product.
        if (identifier?.loanPurposeConfig?.hideAdditionalInfoStep) {
            return false;
        }
        const finalSubmitPageConfig = identifier?.loanPurposeConfig?.finalSubmitPages?.page1 ||
            identifier?.loanPurposeConfig?.finalSubmitPages;
        return !!finalSubmitPageConfig?.coBoAdditionalInfoOnReview;
    }

    performApplicationNextAction(identifier: KeyIdentifierModel, isProvision = false): any {
        let navigationAction: string = '';
        let screenAction: string = '';
        let errorCode: string = '';
        console.log('performApplicationNextAction key identifier =>', identifier);
        switch (identifier.applicationStatus) {
            case ApplicationStatusEnum.OTP_VERIFICATION_PENDING:
            case ApplicationStatusEnum.GENERATE_OFFER_NOT_INITIATED:
                navigationAction = '/';
                screenAction = '';
                break;
            case ApplicationStatusEnum.PRIMARY_APPLICANT_ADDED:
                navigationAction = '/app';
                screenAction = 'co-borrower';
                break;
            case ApplicationStatusEnum.APPLICATION_SUBMITTED:
                navigationAction = '/offer';
                screenAction = 'GENERATE_OFFER_NOT_INITIATED';
                break;
            case ApplicationStatusEnum.NO_OFFER_FOUND:
            case ApplicationStatusEnum.NO_OFFER_HARD_DECLINE:
            case ApplicationStatusEnum.NO_OFFER_SOFT_DECLINE:
            case ApplicationStatusEnum.NO_OFFER_PRE_FAIL:
            case ApplicationStatusEnum.PEND:
            case ApplicationStatusEnum.FRAUD:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.applicationStatus || 'NO_OFFER';
                break;

            case ApplicationStatusEnum.OFFER_GENERATED:
                screenAction = 'manage-offer';
                navigationAction = '/manage-offer';
                // this.router.navigate(['/offer'], {relativeTo: this.activatedRoute});
                break;
            case ApplicationStatusEnum.OFFER_SELECTED:
                navigationAction = '/review';
                screenAction = 'review';
                break;
            // Send customer to offer screen
            case ApplicationStatusEnum.FINANCIAL_SUBMITTED_FAILED:
                errorCode = identifier.offerStatus || 'FINANCIAL_SUBMITTED_FAILED';
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                break;

            case ApplicationStatusEnum.FINANCIAL_SUBMITTED:
                if (!identifier.losStatus) {
                    navigationAction = '/thankyou';
                    screenAction = 'thankyou';
                    errorCode = identifier.applicationStatus;
                } else {
                    // Send the application to loan summary/vendor screen
                    navigationAction = '/provision';
                    screenAction = 'provision';
                }
                break;
            case ApplicationStatusEnum.PRE_FINANCIAL_SUBMITTED:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.applicationStatus;
                break;
            case ApplicationStatusEnum.ACCOUNT_SUBMITTED:
                if (identifier.losStatus) {
                    navigationAction = '/provision';
                    screenAction = 'provision';
                } else {
                    navigationAction = '/thankyou';
                    screenAction = 'thankyou';
                    errorCode = identifier.applicationStatus;
                }
                break;

            case undefined:
            case null:
            default:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = 'EXCEPTION';
                break;
        }
        console.log('Key method to identify somehow we land at => ', {
            navigationAction: navigationAction,
            screenAction: screenAction,
            errorCode: errorCode
        });
        console.log('navigation => navigationAction', navigationAction, screenAction);
        return {navigationAction: navigationAction, screenAction: screenAction, errorCode: errorCode};
    }

    performApplicationNextActionFlow2(identifier: KeyIdentifierModel, isProvision = false): any {
        let navigationAction: string = '';
        let screenAction: string = '';
        let errorCode: string = '';

        console.log('performApplicationNextAction key identifier =>', identifier);
        switch (identifier.applicationStatus) {
            case ApplicationStatusEnum.APPLICATION_SUBMITTED: {
                // LP-1748: when the add-income page is disabled for this partner, skip it and
                // route to the same next step used when income has already been collected.
                const skipAddIncomePage = this.appConfig?.addIncomePage?.enabled === false;
                const showEligibilityScreen = !!(identifier.primaryApplicant && identifier.primaryApplicant.employers.length);
                if (skipAddIncomePage || showEligibilityScreen) {
                    screenAction = 'set-eligibility';
                    navigationAction = '/set-eligibility';
                } else {
                    screenAction = 'add-income';
                    navigationAction = '/add-income';
                }
                break;
            }
            case ApplicationStatusEnum.NO_OFFER_FOUND:
            case ApplicationStatusEnum.NO_OFFER_HARD_DECLINE:
            case ApplicationStatusEnum.NO_OFFER_SOFT_DECLINE:
            case ApplicationStatusEnum.NO_OFFER_PRE_FAIL:
            case ApplicationStatusEnum.FRAUD:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.applicationStatus || identifier.offerStatus || 'NO_OFFER';
                break;
            case ApplicationStatusEnum.PEND:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.offerStatus || 'PEND';
                break;
            case ApplicationStatusEnum.OFFER_SELECTED:
                if (environment.institutionId !== 'SCU') {
                    screenAction = 'review';
                    navigationAction = '/review';
                } else {
                    if (this.appConfig.hideAgreementsForCoBorrower && identifier.isCoBoConsentFlow) {
                        navigationAction = '/provision';
                        screenAction = 'provision';
                    } else {
                        screenAction = 'agreement';
                        navigationAction = '/agreement';
                    }
                }
                break;
            case ApplicationStatusEnum.FINANCIAL_SUBMITTED_FAILED:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.applicationStatus;
                break;
            case ApplicationStatusEnum.FINANCIAL_SUBMITTED:
                // Send the application to provisioning module
                navigationAction = '/provision';
                screenAction = 'provision';
                break;
            case ApplicationStatusEnum.PRE_FINANCIAL_SUBMITTED:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = identifier.applicationStatus;
                break;
            case undefined:
            case null:
            default:
                navigationAction = '/thankyou';
                screenAction = 'thankyou';
                errorCode = 'EXCEPTION';
                break;
        }
        console.log('Key method to identify somehow we land at => ', {
            navigationAction: navigationAction,
            screenAction: screenAction,
            errorCode: errorCode
        });
        return {navigationAction: navigationAction, screenAction: screenAction, errorCode: errorCode};
    }


    copyKeyIdentifier(app: ApplicationModel, identifier: KeyIdentifierModel): KeyIdentifierModel {
        console.log('copy key identifier started =>', new Date().getTime());
        identifier.applicationId = app.applicationId;
        identifier.applicationType = app.applicationType;
        identifier.applicationStatus = app.status;
        identifier.loanPurpose = app.loanPurpose;
        identifier.productType = identifier.productName = identifier.identifyProductType(app.loanPurpose);

        if (!(app.applicants === undefined || app.applicants === null)) {
            if (app.applicants.length > 0) {
                const applicant = app.applicants.find(item => item.applicantType === ApplicantType.PRIMARY);
                identifier.applicantFirstName = applicant.firstName;
            }
        }
        identifier.optedAch = app.ach;
        identifier.tracker = 'copyKeyIdentifier';
        console.log('copyKeyIdentifier completed and triggered the subscribe => ', new Date().getTime());
        return identifier;
    }

    mapErrorCodes(_error: any, identifier: KeyIdentifierModel = new KeyIdentifierModel(false)) {
        console.log('something happened, error is thrown => ', _error);
        let error = _error;
        if (_error instanceof HttpErrorResponse) {
            error = _error.error;
        }
        identifier.errorCode = error.code;
        identifier.errorMessage = error.message;
        identifier.tracker = 'errorCodes';
        // this.keyIdentifier.next(this.keyIdentifierData);
        switch (error.code) {
            case 9001:
            case 9002:
            // Exception Occurred while getting Bureau Report from TransUnion.
            // 700 API call failed.
            case 9003:
            // Exception Occurred while getting Bureau Report from 700.
            case 9004:
            // Drools PostBureau PreEligibility API call failed.
            case 9006:
            // Exception Occurred while executing PostBureau PreEligibility in Drools.
            case 9007:
            // Drools Rates API call failed.
            case 9008:
            // Exception Occurred while executing Rate rules in Drools.
            case 4003:
            case 40051:
            case 8888:
                // Unexpected Error Occurred while processing request.
                identifier.errorCode = 'EXCEPTION';
                this.router.navigate(['/thankyou']);
                break;
            case 6003:
                // Date format Exception occurred
                break;
            case 7001:
            case 7002:
                identifier.errorCode = 'NO_VIN';
                this.router.navigate(['/trade']);
                console.log('Navigating to vehicle screen...');
                break;
            case 5001:
            case 5002:
                // input or Mandatory Field is Null
                break;
            case 5003:
                // input or Mandatory Field is Null
                break;
            case 5004:
                this.router.navigate(['/thankyou']);
                identifier.errorCode = 'NO_OFFER';
                console.log('Navigating to vehicle screen...');
                break;
        }
        this.keyIdentifier.next(identifier);
    }

    GetObjectPropertyValue<T, K extends keyof T>(obj: T, key: K) {
        return obj[key];
    }
}
